import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  ArrowRight,
  Image,
  Link as LinkIcon,
  Github,
  Figma,
  X,
  Loader2,
  Rocket,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { projectAPI, categoryAPI, uploadAPI } from '../lib/api';
import { LEGAL_DISCLAIMER, isValidUrl } from '../lib/utils';

export function SubmitProjectPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdProject, setCreatedProject] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    tagline: '',
    description: '',
    category: '',
    demo_url: '',
    github_url: '',
    figma_url: '',
    image_url: '',
    tags: []
  });

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/submit');
      return;
    }

    const fetchCategories = async () => {
      try {
        const response = await categoryAPI.list();
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, [isAuthenticated, navigate]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 5) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  const validateStep = (stepNum) => {
    const newErrors = {};

    if (stepNum === 1) {
      if (!formData.title || formData.title.length < 5) {
        newErrors.title = 'Title must be at least 5 characters';
      }
      if (!formData.tagline || formData.tagline.length < 10) {
        newErrors.tagline = 'Tagline must be at least 10 characters';
      }
      if (!formData.category) {
        newErrors.category = 'Please select a category';
      }
    }

    if (stepNum === 2) {
      if (!formData.description || formData.description.length < 50) {
        newErrors.description = 'Description must be at least 50 characters';
      }
    }

    if (stepNum === 3) {
      if (formData.demo_url && !isValidUrl(formData.demo_url)) {
        newErrors.demo_url = 'Please enter a valid URL';
      }
      if (formData.github_url && !isValidUrl(formData.github_url)) {
        newErrors.github_url = 'Please enter a valid URL';
      }
      if (formData.figma_url && !isValidUrl(formData.figma_url)) {
        newErrors.figma_url = 'Please enter a valid URL';
      }
      if (formData.image_url && !isValidUrl(formData.image_url)) {
        newErrors.image_url = 'Please enter a valid image URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setLoading(true);

    try {
      const response = await projectAPI.create(formData);
      setCreatedProject(response.data);
      setSuccess(true);
      toast.success('Project submitted successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit project';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success && createdProject) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Project Submitted!
          </h1>
          <p className="text-slate-600 mb-8">
            Your project is now live. Start sharing it to build momentum!
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate(`/project/${createdProject.project_id}`)}
              className="w-full btn-boost"
              data-testid="view-project-btn"
            >
              View Your Project
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/discover')}
              className="w-full rounded-full"
            >
              Back to Discover
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Submit Your Project</h1>
              <p className="text-slate-600 text-sm">Step {step} of 3</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6 flex gap-2">
            {[1, 2, 3].map((s) => (
              <div 
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  s <= step ? 'bg-orange-500' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          {step === 1 && (
            <StepOne
              formData={formData}
              updateField={updateField}
              categories={categories}
              errors={errors}
              tagInput={tagInput}
              setTagInput={setTagInput}
              addTag={addTag}
              removeTag={removeTag}
            />
          )}

          {step === 2 && (
            <StepTwo
              formData={formData}
              updateField={updateField}
              errors={errors}
            />
          )}

          {step === 3 && (
            <StepThree
              formData={formData}
              updateField={updateField}
              errors={errors}
            />
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            {step > 1 ? (
              <Button variant="outline" onClick={prevStep} className="rounded-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <Button onClick={nextStep} className="btn-boost" data-testid="next-step-btn">
                Next Step
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                className="btn-boost" 
                disabled={loading}
                data-testid="submit-project-btn"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Submit Project
              </Button>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="disclaimer-banner mt-6">
          {LEGAL_DISCLAIMER}
        </div>
      </div>
    </div>
  );
}

function StepOne({ formData, updateField, categories, errors, tagInput, setTagInput, addTag, removeTag }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Basic Information</h2>
        <p className="text-slate-600 text-sm">Tell us about your project</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="title">Project Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder="My Awesome Project"
            className={errors.title ? 'border-red-500' : ''}
            data-testid="input-title"
          />
          {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
        </div>

        <div>
          <Label htmlFor="tagline">Tagline *</Label>
          <Input
            id="tagline"
            value={formData.tagline}
            onChange={(e) => updateField('tagline', e.target.value)}
            placeholder="A short, catchy description of your project"
            className={errors.tagline ? 'border-red-500' : ''}
            data-testid="input-tagline"
          />
          {errors.tagline && <p className="text-red-500 text-xs mt-1">{errors.tagline}</p>}
        </div>

        <div>
          <Label htmlFor="category">Category *</Label>
          <Select value={formData.category} onValueChange={(val) => updateField('category', val)}>
            <SelectTrigger className={errors.category ? 'border-red-500' : ''} data-testid="select-category">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
        </div>

        <div>
          <Label>Tags (optional, max 5)</Label>
          <div className="flex gap-2 mb-2 flex-wrap">
            {formData.tags.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 rounded-full text-sm">
                {tag}
                <button onClick={() => removeTag(tag)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              placeholder="Add a tag"
              disabled={formData.tags.length >= 5}
            />
            <Button type="button" variant="outline" onClick={addTag} disabled={formData.tags.length >= 5}>
              Add
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepTwo({ formData, updateField, errors }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Tell Your Story</h2>
        <p className="text-slate-600 text-sm">Describe your project in detail</p>
      </div>

      <div>
        <Label htmlFor="description">Description *</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => updateField('description', e.target.value)}
          placeholder="What problem does your project solve? What makes it unique? What stage is it at?"
          rows={12}
          className={errors.description ? 'border-red-500' : ''}
          data-testid="input-description"
        />
        <div className="flex justify-between items-center mt-1">
          {errors.description && <p className="text-red-500 text-xs">{errors.description}</p>}
          <span className={`text-xs ml-auto ${formData.description.length < 50 ? 'text-red-500' : 'text-slate-400'}`}>
            {formData.description.length} / 5000 characters (min 50)
          </span>
        </div>
      </div>
    </div>
  );
}

function StepThree({ formData, updateField, errors }) {
  const [uploading, setUploading] = useState(false);
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('submit.invalidImageType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('submit.imageTooLarge'));
      return;
    }

    setUploading(true);
    try {
      const res = await uploadAPI.uploadImage(file);
      updateField('image_url', res.data.url);
      toast.success(t('submit.imageUploaded'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('submit.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">{t('submit.linksMedia')}</h2>
        <p className="text-slate-600 text-sm">{t('submit.linksMediaDesc')}</p>
      </div>

      <div className="space-y-4">
        {/* Image Upload */}
        <div>
          <Label className="flex items-center gap-2 mb-2">
            <Image className="w-4 h-4" />
            {t('submit.coverImage')}
          </Label>

          {formData.image_url ? (
            <div className="relative aspect-video rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
              <img
                src={formData.image_url}
                alt="Cover"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = ''; }}
              />
              <button
                type="button"
                onClick={() => { updateField('image_url', ''); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="aspect-video rounded-xl border-2 border-dashed border-slate-300 hover:border-orange-400 bg-slate-50 hover:bg-orange-50 flex flex-col items-center justify-center cursor-pointer transition-colors"
            >
              {uploading ? (
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              ) : (
                <>
                  <Image className="w-10 h-10 text-slate-400 mb-3" />
                  <p className="text-sm font-medium text-slate-700">{t('submit.clickToUpload')}</p>
                  <p className="text-xs text-slate-400 mt-1">{t('submit.imageFormats')}</p>
                </>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={handleFileUpload}
          />

          {/* URL input as alternative */}
          <div className="mt-2">
            <p className="text-xs text-slate-400 mb-1">{t('submit.orPasteUrl')}</p>
            <Input
              value={formData.image_url}
              onChange={(e) => updateField('image_url', e.target.value)}
              placeholder="https://example.com/image.jpg"
              className={errors.image_url ? 'border-red-500' : ''}
              data-testid="input-image-url"
            />
          </div>
          {errors.image_url && <p className="text-red-500 text-xs mt-1">{errors.image_url}</p>}
        </div>

        <div>
          <Label htmlFor="demo_url" className="flex items-center gap-2">
            <LinkIcon className="w-4 h-4" />
            {t('submit.demoUrl')}
          </Label>
          <Input
            id="demo_url"
            value={formData.demo_url}
            onChange={(e) => updateField('demo_url', e.target.value)}
            placeholder="https://myproject.com"
            className={errors.demo_url ? 'border-red-500' : ''}
            data-testid="input-demo-url"
          />
          {errors.demo_url && <p className="text-red-500 text-xs mt-1">{errors.demo_url}</p>}
        </div>

        <div>
          <Label htmlFor="github_url" className="flex items-center gap-2">
            <Github className="w-4 h-4" />
            GitHub Repository
          </Label>
          <Input
            id="github_url"
            value={formData.github_url}
            onChange={(e) => updateField('github_url', e.target.value)}
            placeholder="https://github.com/username/repo"
            className={errors.github_url ? 'border-red-500' : ''}
          />
          {errors.github_url && <p className="text-red-500 text-xs mt-1">{errors.github_url}</p>}
        </div>

        <div>
          <Label htmlFor="figma_url" className="flex items-center gap-2">
            <Figma className="w-4 h-4" />
            Figma / Design File
          </Label>
          <Input
            id="figma_url"
            value={formData.figma_url}
            onChange={(e) => updateField('figma_url', e.target.value)}
            placeholder="https://figma.com/file/..."
            className={errors.figma_url ? 'border-red-500' : ''}
          />
          {errors.figma_url && <p className="text-red-500 text-xs mt-1">{errors.figma_url}</p>}
        </div>
      </div>
    </div>
  );
}
